<template>
    <v-card class="NewDocumentDialog">
        <v-toolbar flat color="deep-purple darken-4" dark>
            <v-toolbar-title><strong>{{ collections[collection].name.toUpperCase() }}</strong> (nouvelle entrée)</v-toolbar-title>
            <v-spacer />

            <v-icon
                class="clickable"
                @click.stop="clearDialog"
                right
            >mdi-close</v-icon>
        </v-toolbar>

        <v-card-text class="pt-5">
            <v-switch
                v-if="collections[collection].xls"
                v-model="listMode"
                label="Chargement de liste XLS"
            />

            <v-form
                v-if="Boolean(collections[collection]) && Boolean(listMode)"
                ref="form"
                @submit.prevent="save" 
            >
                <div v-for="field in collections[collection].fields.filter(x => Boolean(x.listLoader))" :key="`field-lCol-${field.name}`">
                    <v-select
                        v-if="field.type === 'select'"
                        v-model="form[field.name]"
                        :items="vendorsSelect"
                        :label="field.title"
                        :prepend-icon="`mdi-${field.icon}`"
                        clearable
                    />

                    <div v-if="field.type === 'file'">
                        <v-file-input                             
                            :label="field.title"
                            accept=".xls,.xlsx"
                            @change="loadXLSFile"
                            clearable
                        ></v-file-input>

                        <xlsx-read :file="file">
                            <xlsx-json @parsed="saveXLSData" />
                        </xlsx-read>
                    </div>
                </div>

                <v-expand-transition>
                    <div 
                        class="d-flex"
                        v-if="Boolean(list.length)"
                    >
                        <v-spacer />
                        <v-btn
                            fab
                            type="submit"
                            color="success"
                        >
                            <v-icon>mdi-check</v-icon>
                        </v-btn>
                    </div>
                </v-expand-transition>
            </v-form>

            <v-form 
                v-if="Boolean(collections[collection]) && !Boolean(listMode)"
                ref="form"
                v-model="valid"
                lazy-validation
                @submit.prevent="save" 
            >
                <v-row class="d-flex justify-center">
                    <v-col cols="6">
                        <div v-for="field in collections[collection].fields.filter(x => !Boolean(x.listLoader))" :key="`field-${field.name}`">
                            <v-text-field
                                v-if="field.type === 'text' && !Boolean(field.validation)"
                                v-model="form[field.name]"
                                :label="field.title"
                                :prepend-icon="`mdi-${field.icon}`"
                                :required="Boolean(field.required)"
                            />

                            <v-text-field
                                v-if="field.type === 'text' && Boolean(field.validation)"
                                v-model="form[field.name]"
                                :label="field.title"
                                :prepend-icon="`mdi-${field.icon}`"
                                :required="Boolean(field.required)"
                                :validate-on-blur="Boolean(field.validation)"
                                :rules="field.validation"
                            />

                            <v-text-field
                                v-if="field.type === 'email'"
                                v-model="form[field.name]"
                                :label="field.title"
                                type="email"
                                :prepend-icon="`mdi-${field.icon}`"
                                :rules="field.validation"
                                required
                            />

                            <v-text-field
                                v-if="field.type === 'phone'"
                                v-model="form[field.name]"
                                :label="field.title"
                                type="tel"
                                :prepend-icon="`mdi-${field.icon}`"
                                :rules="field.validation"
                                required
                            /> 

                            <v-textarea
                                v-if="field.type === 'textarea' && !Boolean(field.validation)"
                                v-model="form[field.name]"
                                :label="field.title"
                                :prepend-icon="`mdi-${field.icon}`"
                                :required="Boolean(field.required)"
                            />
                        </div> 
                    </v-col>

                    <v-col cols="6">
                        <div v-for="field in collections[collection].fields.filter(x => !Boolean(x.listLoader))" :key="`field-lCol-${field.name}`">
                            <v-btn-toggle
                                v-if="field.type === 'checkboxes'"
                                v-model="form[field.name]"
                                tile
                                group
                                color="deep-purple darken-4"
                            >
                                <v-btn v-for="option in field.options" :key="`option-${option.value}`" :value="option.value">
                                    {{ option.text }}
                                </v-btn>
                            </v-btn-toggle>

                            <v-expand-transition>
                                <div v-if="field.type === 'tags' && addTag.show !== field.name" class="mt-2">
                                    <span class="subheading">Catégorie(s) applicable(s)</span>
                                    <v-chip-group
                                        column
                                        multiple
                                        v-model="form[field.name]"
                                        active-class="deep-purple darken-4 white--text"
                                    >
                                        <v-chip
                                            v-for="option in field.options" 
                                            :key="`option-${option.value}`"
                                            :value="option.value"
                                        >
                                            {{ option.text }}
                                        </v-chip>

                                        <v-btn
                                            fab
                                            x-small
                                            class="deep-purple darken-4 ml-5 my-1"
                                            dark
                                            text
                                            @click="addTag.show = addTag.show === field.name ? null : field.name"
                                        >
                                            <v-icon>mdi-plus</v-icon>
                                        </v-btn>
                                    </v-chip-group>
                                </div>    
                            </v-expand-transition>   
                            
                            <v-expand-transition>
                                <div v-if="addTag.show === field.name" class="d-flex align-center">
                                    <v-text-field
                                        v-model="addTag.text"
                                        prepend-icon="mdi-tag-text"
                                        label="Nom de la catégorie"
                                        :icon="`mdi-${field.icon}`"
                                    />
                                    <v-spacer></v-spacer>

                                    <v-text-field
                                        v-model="addTag.value"
                                        prepend-icon="mdi-pound"
                                        :messages="[`Valeur du tag`]"
                                    />
                                    <v-spacer></v-spacer>

                                    <v-btn
                                        fab
                                        x-small
                                        class="success"
                                        dark
                                        @click="createTag"
                                    >
                                        <v-icon>mdi-check</v-icon>
                                    </v-btn>
                                </div>
                            </v-expand-transition> 
                        </div> 
                    </v-col>
                </v-row>
                
                <v-expand-transition>
                    <div 
                        class="d-flex"
                        v-if="allowSave"
                    >
                        <v-spacer />
                        <v-btn
                            fab
                            type="submit"
                            color="success"
                        >
                            <v-icon>mdi-content-save</v-icon>
                        </v-btn>
                    </div>
                </v-expand-transition>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<script>
import XLSX from 'xlsx'
import { XlsxRead, XlsxJson, XlsxTable } from "@/components/vue-xlsx/dist/vue-xlsx.es.js"

export default {
    name: 'NewDocumentDialog',
    props: [
        'collection',
    ],
    components: {
        XlsxRead,
        XlsxJson,
        XlsxTable
    },
    data: () => ({
        addTag: {
            show: null,
            text: null,
            value: null,
        },
        listMode: false,
        file: null,
        list: [],
        files: {},
        valid: true,
        form: {},
        loading: false,
        meta: {},
        logos: [],
        updated: false,
    }),
    computed: {
        allowSave() {
            let allow = true

            let required = this.collections[this.collection].fields.filter(x => x.required)

            required.forEach(x => {
                if(!Boolean(this.form[x.name])) allow = false
            })

            return allow
        },
        items() {
            return Object.entries(this.$store.state[this.collection].items)
        },
        vendors() {
            return Object.entries(this.$store.state.vendors.items)
        },
        vendorsSelect() {
            let vendors = []

            switch(this.collection) {
                case 'leasing':
                    vendors = this.vendors.filter(vendor => vendor[1].categories.indexOf('LEASE') > -1)
                    break

                default:
                    vendors = this.vendors.filter(vendor => vendor[1].categories.indexOf('LEASE') === -1)
            }
            
            return vendors.map(vendor => {
                return {
                    value: vendor[0],
                    text: this.getVendorsNameById(vendor[0]),
                }
            })
        },
        categories() {
            let arr = []

            if(Boolean(this.$store.state.app.items)) {
                Object.entries(this.$store.state.app.items.categories).forEach(obj => {
                    arr.push({ value: obj[0], text: obj[1] })
                })
            }
            
            return this.$p9.objects.sortList(arr, 'text', 'asc')
        },
        collections() {
            return Boolean(this.categories) ? {
                items: {
                    name: 'Items de vente',
                    xls: true,
                    fields: [

                    ]
                },
                cpp: {
                    name: 'Contrats de service',
                    xls: true,
                    fields: []
                },
                leasing: {
                    name: 'Financement',
                    xls: true,
                    fields: [
                        {
                            name: 'vendor',
                            title: 'Nom du fournisseur',
                            type: 'select',
                            icon: 'office-building',
                            required: true,
                            listLoader: true,
                        },
                        {
                            name: 'xlsFile',
                            title: 'Charte 0% en format Excel',
                            type: 'file',
                            icon: 'file-excel',
                            required: true,
                            listLoader: true,
                        }
                    ]
                },
                opportunities: {
                    name: 'Opportunités',
                    xls: false,
                    fields: [
                        {
                            name: 'client',
                            title: 'Client',
                            type: 'text',
                            icon: 'office-building',
                            required: true,
                        },
                        {
                            name: 'desc',
                            title: 'Description',
                            type: 'textarea',
                            icon: 'dots-horizontal',
                            required: true,
                        },
                    ],
                },
                vendors: {
                    name: 'Fournisseurs',
                    xls: false,
                    fields: [
                        {
                            name: 'name',
                            title: 'Nom du fournisseur',
                            type: 'text',
                            icon: 'office-building',
                            required: true,
                        },
                        {
                            name: 'url',
                            title: 'Site web du fournisseur',
                            type: 'text',
                            icon: 'web',
                            validation: [ v => this.$p9.test.isUrl(v) || `Vous devez entrer une URL valide.`]
                        },
                        {
                            name: 'categories',
                            title: 'Catégorie(s)',
                            type: 'tags',
                            icon: 'tag-multiple',                            
                            options: this.categories,
                            required: true,
                        },
                        {
                            name: 'contactName',
                            title: 'Nom du contact',
                            type: 'text',
                            icon: 'account-tie',                             
                            required: true,
                        },
                        {
                            name: 'contactPhone',
                            title: 'Téléphone cellulaire du contact',
                            type: 'text',
                            icon: 'cellphone-iphone',                             
                            validation: [ v => Boolean(v) && v.replace(/[^0-9]/g, '').match(/[0-9]{10,15}/).length > 0 || `Vous devez entrer un numéro de téléphone valide.`],
                            required: true,
                        },
                        {
                            name: 'contactEmail',
                            title: 'Courriel du contact',
                            type: 'text',
                            icon: 'at',    
                            validation: [
                                v => !!v || 'E-mail is required',
                                v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
                            ],
                        }
                    ],
                    files: [
                        {
                            name: 'logo',
                            type: ''
                        }
                    ]
                }
            } : []         
            
        },
        siblings() {
            return this.$store.state[this.collection].items
        },
        enableDelete() {
            return true
        },
        newDBEntry() {
            let data = {}

            switch(this.collection) {
                case 'opportunities':
                    data = {
                        author: this.$store.state.auth.user.id,
                        client: this.$p9.str.capitalize(this.form.client.trim()),
                        desc: this.form.desc
                    }
                    break

                case 'leasing':
                    data = {
                        vendor: this.form.vendor,
                        chart: this.list,
                    }
                    break

                case 'vendors':
                    data = {
                        active: true,
                        categories: this.form.categories,
                        name: this.$p9.str.capitalize(this.form.name.trim()),
                        url: this.form.url.trim(),
                        contacts: [
                            {
                                name: this.form.contactName.trim(),
                                cellPhone: this.form.contactPhone.replace(/[^0-9]/g, ''),
                                email: this.form.contactEmail.trim(),
                            }
                        ]
                    }
                    break

                default:
            }

                    
            return data
        },
    },
    methods: {
        log(msg) {
            console.log(msg)
        },
        getVendorById(id) {                
            return this.vendors.filter( vendor => vendor[0] === id )[0]
        },
        getVendorsNameById(id) {                
            return Boolean(this.getVendorById(id)) ? this.getVendorById(id)[1].name : null
        },
        loadXLSFile() {
            this.file = event.target.files ? event.target.files[0] : null
        },
        saveXLSData(list) {
            this.list = this.formatListData(list)
        },
        formatListData(list) {
            switch(this.collection) {
                case 'leasing':
                    let arr = Boolean(this.items[this.form.vendor]) ? this.items[this.form.vendor] : [] 

                    list.forEach(item => {
                        let term = Number(item.pmt.replace(/[A-z]/g, '')) * Number(item.pmt.replace(/[0-9]/g, '').replace("M", "1").replace("Q", "3"))
                        let freq = item.pmt.replace(/[0-9]/g, '')
                        let buyout = item.buyout

                        let row = {
                            term: term,
                            frequency: freq,
                            buyout: buyout,
                        }

                        Object.keys(item).filter(item => item.includes("@")).forEach(r => { 
                            let range = {
                                min: r.split("@")[0],
                                max: r.split("@")[1],
                                terms: [],
                            }
                            
                            row.factor = Number(item[r])

                            //Créer une entrée pour ce range s'il n'en existe pas déjà une
                            let target = arr.filter(x => x.min === range.min && x.max === range.max)
                            if(!Boolean(target.length)) arr.push(range)

                            target = arr.filter(x => x.min === range.min && x.max === range.max)[0].terms
                            target.push(row)

                            //if(target.filter(x => x.term === term && x.frequency === freq && x.buyout === buyout).length === 0) target.push(row)
                        })
                    })

                    return arr
                    break

                default:
                    return []
            }
        },
        createTag() {
            let valueExists = Boolean(this.categories.filter(cat => cat.value === this.addTag.value.toUpperCase()).length)

            if(valueExists) console.log(`Existing value! Try something else!`)
            else {
                let data = [
                    'categories',
                    { ...this.$store.state.app.items.categories, [ this.addTag.value.toUpperCase() ]: this.$p9.str.capitalize(this.addTag.text) }
                ]
                
                this.$store.dispatch(`app/update`, data)
                .then(() => {
                    this.addTag = {
                        show: null,
                        text: null,
                        value: null,
                    }
                })
                .catch(err => console.log(err.message))                
            }

        },
        toggleAddTag(tag) {
            this.addTag[tag] = !Boolean(this.addTag[tag])
        },
        prompt() {
            console.log('prompting...')
        },
        save() {
            this.$store.dispatch(`${this.collection}/add`, this.newDBEntry)
                .then(() => { this.clearDialog() })
                .catch(err => console.log(err.message))
        },
        clearDialog() {
            this.reset()
            this.$emit('close')
        },
        reset() {
            this.updated = false
            this.newTag = {}
            this.files = {}
            this.form = {}
            this.loading = false
            this.meta = {}
            this.logos = []
            this.updated = false
        }
    },
    destroyed() {
        this.reset()
    }
}
</script>

<style scoped>
   
</style>